import type { PageCatchUpState } from './syncEngine'

// Where each kind of pull got to, kept on this device so the next pull asks
// only for what changed since. Tree and settings cursors are server change
// numbers; page catch-up keeps its own state (see pageCatchUp.ts).

/**
 * Server-assigned tree change number per workspace. The old HLC watermarks
 * lived under 'ink-tree-watermark-'; a new prefix makes every device start
 * with one full pull instead of misreading a clock value as a cursor.
 */
const TREE_CURSOR_PREFIX = 'ink-tree-cursor-'
/** Server settings change number per user; see SyncEngine.pullSettings(). */
const SETTINGS_CURSOR_PREFIX = 'ink-settings-cursor-'
/** Page catch-up's page-heads cursor and deferred pages, per workspace. */
const PAGE_CATCH_UP_PREFIX = 'ink-page-catch-up-'

/** Tree cursors per workspace, cached in memory after the first read. */
export class TreeCursorStore {
  private cursors: Map<string, number> = new Map()

  /**
   * The last tree change number this device has applied for a workspace, or
   * null when it has never completed a pull (which makes the next pull full).
   */
  get(workspaceId: string): number | null {
    if (this.cursors.has(workspaceId)) {
      return this.cursors.get(workspaceId)!
    }
    if (typeof localStorage !== 'undefined') {
      try {
        const stored = localStorage.getItem(`${TREE_CURSOR_PREFIX}${workspaceId}`)
        if (stored !== null && /^\d+$/.test(stored)) {
          const val = Number(stored)
          this.cursors.set(workspaceId, val)
          return val
        }
      } catch {}
    }
    return null
  }

  /** Stores the cursor returned by the server with a tree pull. */
  set(workspaceId: string, cursor: number): void {
    this.cursors.set(workspaceId, cursor)
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(`${TREE_CURSOR_PREFIX}${workspaceId}`, cursor.toString())
      } catch {}
    }
  }

  /** Forgets one workspace's tree cursor. */
  clear(workspaceId: string): void {
    this.cursors.delete(workspaceId)
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(`${TREE_CURSOR_PREFIX}${workspaceId}`)
      } catch {}
    }
  }

  /** Forgets every tree, settings and page catch-up cursor (used on sign-out). */
  clearAll(): void {
    this.cursors.clear()
    if (typeof localStorage !== 'undefined') {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i)
          if (key && (
            key.startsWith(TREE_CURSOR_PREFIX) ||
            key.startsWith(SETTINGS_CURSOR_PREFIX) ||
            key.startsWith(PAGE_CATCH_UP_PREFIX) ||
            key.startsWith('ink-tree-watermark-')
          )) {
            localStorage.removeItem(key)
          }
        }
      } catch {}
    }
  }
}

export function getSettingsCursor(userId: string): number | null {
  try {
    const stored = localStorage.getItem(`${SETTINGS_CURSOR_PREFIX}${userId}`)
    return stored !== null && /^\d+$/.test(stored) ? Number(stored) : null
  } catch {
    return null
  }
}

export function setSettingsCursor(userId: string, cursor: number): void {
  try {
    localStorage.setItem(`${SETTINGS_CURSOR_PREFIX}${userId}`, cursor.toString())
  } catch {}
}

/**
 * What page catch-up has handled in a workspace: the page-heads cursor (null
 * before the first complete pass) and pages it skipped while they were in
 * the Trash, to catch up if they come back.
 */
export function getPageCatchUpState(workspaceId: string): PageCatchUpState {
  try {
    const stored = JSON.parse(localStorage.getItem(`${PAGE_CATCH_UP_PREFIX}${workspaceId}`) || 'null')
    return {
      cursor: Number.isSafeInteger(stored?.cursor) && stored.cursor >= 0 ? stored.cursor : null,
      deferred: Array.isArray(stored?.deferred) ? stored.deferred.filter((id: unknown): id is string => typeof id === 'string') : [],
    }
  } catch {
    return { cursor: null, deferred: [] }
  }
}

export function setPageCatchUpState(workspaceId: string, state: PageCatchUpState): void {
  try {
    localStorage.setItem(`${PAGE_CATCH_UP_PREFIX}${workspaceId}`, JSON.stringify(state))
  } catch {}
}
